import Immutable from 'immutable';
import { cloneDeep } from 'lodash';
import type { AspectRatio, Scene, TimeAwareMediaAsset } from '@lumen5/luminary';
import Luminary, { VideoData } from '@lumen5/luminary';

import type { BoundingBox, SceneAttributesResponse } from 'js/src/client';
import type { TimingMetadata } from 'js/src/components/app/OverlaySelector';
import type { SwapSceneDesignTabRenderedDataMap } from 'js/src/components/app/SwapSceneDesignTab';
import { Toast } from 'js/src/components/common/Toast';
import {
    clearTrackedSlideTextAssets,
    getSwapSceneDesignSlideId,
    setSwapSceneDesignForSlide,
} from 'js/src/data/scenes';
import { setFocusedSceneAndScroll } from 'js/src/data/scenes/scenes-operations';
import type { DisplayGroupId, SceneDesignDisplayName, SceneDesignId } from 'js/src/data/templates';
import {
    canUserSeeSceneDesign,
    getSceneDesignDisplayGroup,
    getSceneDesignDisplayName,
    getTemplateBundle,
    getTemplateDefaultSceneDesign,
    getTemplateDefaultVoiceoverSceneDesign,
    getTemplateDisplayGroups,
    getTemplateSceneIds,
    selectSceneSequenceDesigns,
} from 'js/src/data/templates';
import {
    isAssetTimingOverridden,
    setBackgroundColorUserOverride,
    setBrightnessUserOverride,
    setCropUserOverride,
    setKenburnUserOverride,
    setTextAlignmentUserOverride,
    setTextPositionUserOverride,
    setTextSizeUserOverride,
} from 'js/src/data/user-overrides';
import { checkpointVideoOperation } from 'js/src/data/video-data';
import type { AddChartAssetResult } from 'js/src/libs/chart-asset-add';
import { setupChartSceneOperation } from 'js/src/libs/chart-asset-add';
import type { ImmutableMap } from 'js/src/libs/consts';
import {
    DYNAMIC_DISPLAY_GROUPS,
    ImmutableFromJS,
    isNotNull,
    SceneDesignSpecialCase,
} from 'js/src/libs/consts';
import { cropToBoundingBox } from 'js/src/libs/crop-utils';
import { CHART_SCENE_TILES } from 'js/src/libs/default-chart-configs';
import type { AssetPair } from 'js/src/libs/scene-swapping/SceneSwapper';
import SceneSwapper from 'js/src/libs/scene-swapping/SceneSwapper';
import uuid from 'js/src/libs/uuid';
import type { TimingSpecification } from 'js/src/luminary/asset-timing-operations';
import { setAssetIntroExitTimesOperation } from 'js/src/luminary/asset-timing-operations';
import { withDataLuminarySync } from 'js/src/luminary/data-luminary';
import { CreateOverlayError } from 'js/src/luminary/index';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    applyTalkingHeadMediaOperation,
    applyUserSceneDurationOperation,
    deleteSceneGroupOperation,
    duplicateSceneGroupOperation,
    fetchAndSetRecommendedSceneAttributesOperation,
    replaceSceneGroupOperation,
    replaceSlideOperation,
} from 'js/src/luminary/luminary-operations';
import {
    getPrePackagedMorphPosition,
    getPrePackagedSceneSequenceSceneGroupIds,
    getPrePackagedSceneSequenceSceneIds,
    getVoiceDrivenAsset,
    isEligibleForMultipleSpeakers,
    isOutroSceneGroup,
    isTalkingHeadSceneGroup,
    isVoiceDrivenSceneGroup,
    sceneContainsEditableMedia,
    sceneContainsIcon,
} from 'js/src/luminary/luminary-selectors';
import { isVoiceDrivenCompatibleSceneCategory } from 'js/src/luminary/luminary-utils';
import type { SceneSequenceDesign } from 'js/src/luminary/scene-sequence-designs';
import {
    getWordCountInVideoTimeRangeOperation,
    loadSceneSequencePreviewsIntoLocalLuminaryOperation,
    swapToSceneSequenceOperation,
} from 'js/src/luminary/scene-sequence-designs';
import {
    getVoicedrivenSceneDuration,
    getVoicedrivenSceneEndTime,
    getVoicedrivenSceneStartTime,
    runVoicedrivenBatchRunOperation,
    setVoicedrivenSceneDuration,
} from 'js/src/luminary/voicedriven-scenes';

export type TimingDataType = {
    OPEN_0: number | null;
    SCENE_ATTRIBUTES_REQUESTED_1: number | null;
    SCENE_ATTRIBUTES_RECEIVED_2: number | null;
    LUMINARY_INITIALIZED_3: number | null;
    SCENE_DESIGN_RECOMMENDATIONS_FINISHED_4: number | null;
    LUMINARY_STARTING_RENDER_5: number | null;
    LUMINARY_SCENES_ADDED_6: number | null;
    LUMINARY_AUTO_DRAW_ENABLED_7: number | null;
    LUMINARY_RENDERED_FIRST_CATEGORY_8: number | null;
    LUMINARY_FINISHED_RENDERS_9: number | null;
};

export type SwapSceneDesignRenderedData = {
    sceneDesign: string;
    sceneCategory: string;
    displayGroup: number;
    displayName?: SceneDesignDisplayName;
    slide_id: string;
    specialCase: SceneDesignSpecialCase;
    isActive: boolean;
    isRendered: boolean;
    sceneSequenceId?: string;
    sceneSequenceSceneIds?: string[];
    chartType?: string;
};

export const guessSpecialCase = (scene: Scene) => {
    const linkedMediaAssets = scene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE).filter((asset) => {
        return asset.isEditable && asset.linkToSceneGroup === true;
    });
    if (linkedMediaAssets.length > 1) {
        return SceneDesignSpecialCase.MultipleSpeakers;
    }
    return SceneDesignSpecialCase.NONE;
};
/**
 * For performance reasons we don't want to copy too many subscenes to preview.
 * We copy just few of them, and when user selects particular scene design we create new scene with all subscenes
 */
const MAX_SUBSCENES_IN_PREVIEW = 2;

export const applyRecommendedSceneOverridesOperation = createLuminaryOperation(
    (
        { luminary },
        sceneId: string,
        sceneAttributes: Record<SceneDesignId, SceneAttributesResponse> | null,
    ) => {
        const targetScene = luminary.getSceneById(sceneId);
        if (!targetScene) {
            return;
        }

        if (!sceneAttributes) {
            return;
        }

        const sceneAttribute = sceneAttributes[targetScene.sceneDesign];
        if (!sceneAttribute) {
            return;
        }

        const textPositionPreset = targetScene.availableTextPositions.includes(
            sceneAttribute.text_pos,
        )
            ? sceneAttribute.text_pos
            : targetScene.availableTextPositions[0];
        targetScene.applyTextPreset(textPositionPreset);

        sceneAttribute.media_attributes.forEach((attrs) => {
            const asset = targetScene.getAssetByName(attrs.asset_name) as TimeAwareMediaAsset;
            if (!asset) {
                return;
            }

            // Check if the fillmode is something other than cover. If so we shouldn't set any of the
            // media attributes for it since they aren't applicable for non-cover'ed assets.
            // TODO: ideally this would be better to do further upstream to avoid duplicate logic here
            if (asset.fillMode !== VideoData.FILL_COVER) {
                return;
            }
            if (asset.linkToSceneGroup === true) {
                return;
            }

            asset.brightness = attrs.brightness;
            if (attrs.x_offset !== null && attrs.y_offset !== null) {
                // null if no crop suggested
                asset.xTranslation = attrs.x_offset;
                asset.yTranslation = attrs.y_offset;
            }
            asset.kenburn = attrs.background_animation;
        });
    },
);

export const getMultipleSpeakersSceneDesignIdsOperation = createLuminaryOperation(
    (
        { getState, luminary },
        sceneDesignIds: Immutable.List<SceneDesignId>,
        faceDetections: BoundingBox[],
    ): Immutable.List<SceneDesignId> => {
        if (faceDetections.length === 0) {
            return Immutable.List<SceneDesignId>();
        }

        const MAX_OPTIONS_TO_RECOMMEND = 3;

        const dataLuminary = new Luminary({
            fetchResources: false,
            disableRenderer: true,
            autoDraw: false,
            autoUpdate: false,
        });
        try {
            dataLuminary.setTemplate(getTemplateBundle(getState(), luminary.template.id)!);

            const INELIGIBLE_SCORE = -1;
            const calculateScoreFactors = (scene: Scene) => {
                const mediaAssets = scene
                    .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
                    .filter(isEligibleForMultipleSpeakers);

                if (mediaAssets.length <= 1) {
                    // split-and-stack only makes sense if there are multiple media assets in the scene design
                    return {
                        variance: INELIGIBLE_SCORE,
                        facesVsMediaAssetsRatio: INELIGIBLE_SCORE,
                    };
                }

                // favour scene designs where the media assets are of equal sizes
                const mediaAssetSizes = mediaAssets.map(
                    (asset) =>
                        asset.mediaTargetResolution.width * asset.mediaTargetResolution.height,
                );
                const meanSize =
                    mediaAssetSizes.reduce((acc, size) => acc + size, 0) / mediaAssetSizes.length;
                const variance =
                    mediaAssetSizes.reduce((acc, size) => acc + (size - meanSize) ** 2, 0) /
                    mediaAssetSizes.length; // we want to minimize the variance

                // prioritize recommending scene designs where the number of media assets = number of faces
                const facesVsMediaAssetsRatio = Math.abs(
                    1 - faceDetections.length / mediaAssets.length,
                ); // we want this to be as small as possible

                return {
                    variance,
                    facesVsMediaAssetsRatio,
                };
            };

            const scenesAndScoreFactors = sceneDesignIds
                .map((sceneDesignId) => {
                    const { scene } = dataLuminary.addSceneGroup({ sceneDesignId });
                    return {
                        scene,
                        scoreFactors: calculateScoreFactors(scene),
                    };
                })
                .filter(({ scoreFactors }) => scoreFactors.variance !== INELIGIBLE_SCORE);

            // normalize the score factors to be between 0 and 1
            const maxVariance = Math.max(
                ...scenesAndScoreFactors.map(({ scoreFactors }) => scoreFactors.variance),
            );
            const maxFacesVsMediaAssetsRatio = Math.max(
                ...scenesAndScoreFactors.map(
                    ({ scoreFactors }) => scoreFactors.facesVsMediaAssetsRatio,
                ),
            );
            scenesAndScoreFactors.forEach(({ scoreFactors }) => {
                scoreFactors.variance = scoreFactors.variance / maxVariance;
                scoreFactors.facesVsMediaAssetsRatio =
                    scoreFactors.facesVsMediaAssetsRatio / maxFacesVsMediaAssetsRatio;
            });

            // calculate the score
            const scenesAndScores = scenesAndScoreFactors.map(({ scene, scoreFactors }) => {
                return {
                    scene,
                    score: scoreFactors.variance + scoreFactors.facesVsMediaAssetsRatio,
                };
            });

            const sortedScenesAndScores = scenesAndScores.sort((a, b) => a.score - b.score);
            return sortedScenesAndScores
                .map(({ scene }) => scene.sceneDesign)
                .slice(0, MAX_OPTIONS_TO_RECOMMEND);
        } finally {
            dataLuminary.destroy();
        }
    },
);

export const setUpMultipleSpeakersSceneOperation = createLuminaryOperation(
    ({ call }, sceneId: string, faceDetections: BoundingBox[]) => {
        // multiple-speakers-scenes are a special case: we want to set up multiple media assets
        // cropped to each speaker's face, and also clear all the text assets
        call(setCropToMatchFaceDetectionsOperation, sceneId, faceDetections);
        call(clearAllTextOperation, sceneId);
    },
);

const setCropToMatchFaceDetectionsOperation = createLuminaryOperation(
    ({ luminary }, sceneId: string, faceDetections: BoundingBox[]) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        const sceneGroup = scene.sceneGroup;
        if (!sceneGroup) {
            return;
        }
        const voicedrivenAsset = sceneGroup.mediaAsset;
        if (!voicedrivenAsset) {
            return;
        }

        const eligibleMediaAssets = scene
            .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
            .filter(isEligibleForMultipleSpeakers)
            .sort((a, b) => {
                if (a.linkToSceneGroup) {
                    return -Infinity;
                }
                if (b.linkToSceneGroup) {
                    return Infinity;
                }
                return (
                    a.mediaTargetResolution.width * a.mediaTargetResolution.height -
                    b.mediaTargetResolution.width * b.mediaTargetResolution.height
                );
            });

        faceDetections.forEach((faceDetection, i) => {
            const asset = eligibleMediaAssets[i];
            if (!asset) {
                return;
            }
            asset.linkToSceneGroup = true;

            cropToBoundingBox(faceDetection, asset, voicedrivenAsset.sourceData);
        });
    },
);

export const clearAllTextOperation = createLuminaryOperation(({ luminary }, sceneId: string) => {
    const scene = luminary.getSceneById(sceneId);
    if (!scene) {
        return;
    }

    scene.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((asset) => {
        asset.text = '';
    });
});

export const clearAllTextInSceneGroupOperation = createLuminaryOperation(
    ({ luminary, call }, sceneGroupId: string) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        luminary.batchRun(() => {
            sceneGroup.scenes.forEach((scene) => {
                call(clearAllTextOperation, scene.id);
            });
        });
    },
);

/**
 * Initialize local luminary with video level settings from the main luminary.
 * This includes clearing, setting capabilities, template, colors, fonts, watermark, and grading.
 */
const initializeLocalLuminaryOperation = createLuminaryOperation(
    async ({ luminary, getState, call }, localLuminary: Luminary) => {
        // clear the this.localLuminary of any other video that might be inside of it
        await localLuminary.clear();
        // set video level properties
        localLuminary.batchRun(() => {
            localLuminary.capabilities.noBranding = true;
            localLuminary.capabilities.customWatermark = true;
            localLuminary.aspectRatio = luminary.aspectRatio;
            localLuminary.textDirection = luminary.textDirection;
            localLuminary.setTemplate(getTemplateBundle(getState(), luminary.template.id)!);
            localLuminary.template.transitionsEnabled = false;
            localLuminary.template.colors = luminary.template.colors;
            localLuminary.template.fonts = luminary.template.fonts;
            const templateWatermarkSources = luminary.template.watermarkSources;
            const templateWatermarkSettings = luminary.template.watermark.save();
            localLuminary.template.replaceWatermarkSources(cloneDeep(templateWatermarkSources));
            localLuminary.template.watermark.load(templateWatermarkSettings);
            const grading = luminary.template.grading;
            if (grading && localLuminary.template.grading !== grading) {
                localLuminary.template.addGrading();
                localLuminary.template.grading!.source = grading.source;
                localLuminary.template.grading!.intensity = grading.intensity;
            }
        });
    },
);

type SceneDesignToRender = {
    sceneDesignId: SceneDesignId;
    displayGroupId: DisplayGroupId;
    specialCase: SceneDesignSpecialCase;
};
/**
 * We want to render scene designs in "right" order.
 * First off all we render designs in the same display group as initial slide.
 * Then other designs in groups base on display group order.
 */
const getOrderedSceneDesigns = createLuminaryOperation(
    ({ luminary, getState, call }, sceneId: string, faceDetections: BoundingBox[]) => {
        const originalScene = luminary.getSceneById(sceneId);
        if (!originalScene) {
            throw new Error('Invalid sceneId');
        }

        const voiceoverBaseSceneDesignId = getTemplateDefaultVoiceoverSceneDesign(
            getState(),
            luminary.template.id,
        );
        const sceneDesignIds = getTemplateSceneIds(getState(), luminary.template.id).filter(
            (sceneDesignId) => {
                // the user must be able to access the scene design. And also, right now we don't ever allow the base
                // voiceover scene design to be swapped into. Currently, there is no valid use-case for that.
                return (
                    canUserSeeSceneDesign(getState(), sceneDesignId) &&
                    sceneDesignId !== voiceoverBaseSceneDesignId
                );
            },
        );

        const displayGroups = getTemplateDisplayGroups(getState(), luminary.template.id);
        const getSceneDesignsInDisplayGroup = (displayGroupId: DisplayGroupId) => {
            return sceneDesignIds.filter((sceneDesignId) => {
                return getSceneDesignDisplayGroup(getState(), sceneDesignId) === displayGroupId;
            });
        };

        const orderedSceneDesigns: SceneDesignToRender[] = [];
        const initialDisplayGroup = getSceneDesignDisplayGroup(
            getState(),
            originalScene.sceneDesign,
        );

        if (isTalkingHeadSceneGroup(originalScene.sceneGroup) && faceDetections.length > 1) {
            const multipleSpeakersSceneDesignIds = call(
                getMultipleSpeakersSceneDesignIdsOperation,
                sceneDesignIds,
                faceDetections,
            );
            if (multipleSpeakersSceneDesignIds.size > 0) {
                orderedSceneDesigns.push(
                    ...multipleSpeakersSceneDesignIds.map((sceneDesignId) => ({
                        sceneDesignId,
                        specialCase: SceneDesignSpecialCase.MultipleSpeakers,
                        displayGroupId:
                            DYNAMIC_DISPLAY_GROUPS[SceneDesignSpecialCase.MultipleSpeakers],
                    })),
                );
            }
        }

        if (initialDisplayGroup !== undefined) {
            // we place all "siblings designs" first
            orderedSceneDesigns.push(
                ...getSceneDesignsInDisplayGroup(initialDisplayGroup).map((sceneDesignId) => ({
                    sceneDesignId,
                    specialCase: SceneDesignSpecialCase.NONE,
                    displayGroupId: initialDisplayGroup,
                })),
            );
        }

        // then other designs by sortedDisplayGroupList
        displayGroups.forEach((displayGroup) => {
            const sceneDesignsInDisplayGroup = getSceneDesignsInDisplayGroup(
                displayGroup.get('id'),
            );
            if (
                sceneDesignsInDisplayGroup.size > 0 &&
                initialDisplayGroup !== displayGroup.get('id')
            ) {
                orderedSceneDesigns.push(
                    ...sceneDesignsInDisplayGroup.map((sceneDesignId) => ({
                        sceneDesignId,
                        specialCase: SceneDesignSpecialCase.NONE,
                        displayGroupId: displayGroup.get('id'),
                    })),
                );
            }
        });

        return orderedSceneDesigns;
    },
);

// create a slide for each scene design, applying the editable and recommended properties
export const setupSceneGroupForSceneDesigns = createLuminaryOperation(
    (
        { luminary, getState, call },
        sceneId: string,
        localLuminary: Luminary,
        sourceSentenceToUseInstead: string | undefined,
        sceneAttributes: Record<SceneDesignId, SceneAttributesResponse> | null,
        faceDetections: BoundingBox[],
        sceneDesignsToRender: SceneDesignToRender[],
    ) => {
        const originalScene = luminary.getSceneById(sceneId);
        if (!originalScene) {
            throw new Error('Invalid sceneId');
        }
        // extract content to copy over from source scene
        const sceneSwapper = new SceneSwapper(originalScene, sourceSentenceToUseInstead);

        let renderedData: Immutable.List<ImmutableMap<SwapSceneDesignRenderedData>> =
            Immutable.List();

        sceneDesignsToRender.forEach((sceneDesignToRender) => {
            const sceneDesignInfo = localLuminary.getSceneDesignInfo(
                sceneDesignToRender.sceneDesignId,
            );
            if (!sceneSwapper.canSwapToCategory(sceneDesignInfo.sceneCategory)) {
                // we can't swap to this scene design, so don't show it in the selector
                return;
            }

            const previewScene = localLuminary.addSceneGroup({
                sceneDesignId: sceneDesignToRender.sceneDesignId,
            }).scene;

            // copy content (text, media, icons, user-editable settings) over to the target scene
            const assetPairs = sceneSwapper.copyContentToScene(previewScene, {
                maxSubscenesToCopy: MAX_SUBSCENES_IN_PREVIEW,
            });

            call(copyAssetTimingOverridesAfterSwapOperation, assetPairs, {
                sourceSceneId: originalScene.id,
                sourceLuminary: luminary,
                targetSceneId: previewScene.id,
                targetLuminary: localLuminary,
            });

            call(applyRecommendedSceneOverridesOperation, previewScene.id, sceneAttributes, {
                luminary: localLuminary,
            });

            if (sceneDesignToRender.specialCase === SceneDesignSpecialCase.MultipleSpeakers) {
                call(setUpMultipleSpeakersSceneOperation, previewScene.id, faceDetections, {
                    luminary: localLuminary,
                });
            }

            // store the rendered data for this scene option
            const displayGroup = sceneDesignToRender.displayGroupId;
            const selectedSpecialCase = guessSpecialCase(originalScene);

            const newEntry = ImmutableFromJS<SwapSceneDesignRenderedData>({
                sceneDesign: previewScene.sceneDesign,
                sceneCategory: previewScene.sceneCategory!,
                displayGroup,
                displayName: getSceneDesignDisplayName(getState(), previewScene.sceneDesign),
                slide_id: previewScene.id,
                specialCase: sceneDesignToRender.specialCase,
                isActive:
                    previewScene.sceneDesign === originalScene.sceneDesign &&
                    selectedSpecialCase === sceneDesignToRender.specialCase,
                isRendered: false,
            });
            renderedData = renderedData.push(newEntry);
        });

        return renderedData;
    },
);

const copyAssetTimingOverridesAfterSwapOperation = createLuminaryOperation(
    (
        { getState, call },
        assetPairs: AssetPair[],
        {
            sourceSceneId,
            targetSceneId,
            sourceLuminary,
            targetLuminary,
        }: {
            sourceSceneId: string;
            targetSceneId: string;
            sourceLuminary: Luminary;
            targetLuminary: Luminary;
        },
    ) => {
        const assetPairsWritten: AssetPair[] = [];
        const timingSpecifications: TimingSpecification[] = [];
        assetPairs.forEach((assetPair) => {
            const sourceAssetId = assetPair.sourceAssetId;
            const targetAssetId = assetPair.targetAssetId;

            const sourceAssetHasCustomTiming = isAssetTimingOverridden(
                getState(),
                sourceSceneId,
                sourceAssetId,
            );
            if (!sourceAssetHasCustomTiming) {
                return;
            }
            const sourceScene = sourceLuminary.getSceneById(sourceSceneId);
            if (!sourceScene) {
                return;
            }
            const sourceAsset = sourceScene.getAssetById(sourceAssetId);
            if (!sourceAsset) {
                return;
            }
            if (!sourceAsset.luminaryParent) {
                return;
            }
            timingSpecifications.push({
                sceneId: targetSceneId,
                assetId: targetAssetId,
                introTime: sourceAsset.appliedIntroAnimationOffset,
                exitTime:
                    sourceAsset.luminaryParent.duration - sourceAsset.appliedOutroAnimationOffset,
            });

            assetPairsWritten.push(assetPair);
        });
        call(
            setAssetIntroExitTimesOperation,
            {
                timingSpecifications,
                operatingOnMainLuminary: false,
                systemTriggered: true,
                parentActionGroup: uuid(),
            },
            {
                luminary: targetLuminary,
            },
        );
        return assetPairsWritten;
    },
);

export const setupLocalLuminaryByDisplayGroupsOperation = createLuminaryOperation(
    async (
        { luminary, getState, call },
        sceneId: string,
        localLuminary: Luminary,
        sourceSentenceToUseInstead: string | undefined,
        sceneAttributes: Record<SceneDesignId, SceneAttributesResponse> | null,
        faceDetections: BoundingBox[],
        abortController: AbortController,
        setTimingData: (key: keyof TimingDataType) => void,
        setRenderedData: (data: SwapSceneDesignTabRenderedDataMap) => void,
        morphOnlyMode = false,
    ) => {
        const originalScene = luminary.getSceneById(sceneId);
        if (!originalScene) {
            throw new Error('Invalid sceneId');
        }

        setTimingData('LUMINARY_INITIALIZED_3');
        await call(initializeLocalLuminaryOperation, localLuminary);
        const orderedSceneDesigns = (() => {
            const allSceneDesigns = call(getOrderedSceneDesigns, sceneId, faceDetections);
            if (!morphOnlyMode) {
                return allSceneDesigns;
            }
            const originalSceneDesignId = originalScene.sceneDesign;
            const templateBundle = getTemplateBundle(getState(), luminary.template.id);
            return allSceneDesigns.filter((sceneDesignToRender) => {
                if (sceneDesignToRender.sceneDesignId === originalSceneDesignId) {
                    return false;
                }
                return withDataLuminarySync(
                    (testLuminary) => {
                        testLuminary.addScene({ sceneDesignId: originalSceneDesignId });
                        testLuminary.addScene({
                            sceneDesignId: sceneDesignToRender.sceneDesignId,
                        });
                        return testLuminary.scenes[0]?.canMorph() ?? false;
                    },
                    { templateBundle: templateBundle ?? undefined },
                );
            });
        })();
        const sceneSwapper = new SceneSwapper(originalScene, sourceSentenceToUseInstead);

        // sort by display group
        let orderedByDisplayGroup: Immutable.OrderedMap<
            DisplayGroupId,
            Immutable.List<SceneDesignToRender>
        > = Immutable.OrderedMap();
        orderedSceneDesigns.forEach((sceneDesignToRender) => {
            const sceneDesignInfo = localLuminary.getSceneDesignInfo(
                sceneDesignToRender.sceneDesignId,
            );
            if (!sceneSwapper.canSwapToCategory(sceneDesignInfo.sceneCategory)) {
                // we can't swap to this scene design, so don't show it in the selector
                return;
            }
            const displayGroup = sceneDesignToRender.displayGroupId;
            orderedByDisplayGroup = orderedByDisplayGroup.update(
                displayGroup,
                Immutable.List(),
                (list) => list.push(sceneDesignToRender),
            );
        });
        setTimingData('SCENE_DESIGN_RECOMMENDATIONS_FINISHED_4');

        // Initialize structure with all display groups and placeholder entries
        let allRenderedData: SwapSceneDesignTabRenderedDataMap = Immutable.OrderedMap();
        orderedByDisplayGroup.forEach((sceneDesignsInGroup, displayGroup) => {
            const placeholderCount = sceneDesignsInGroup.size;
            const placeholders = Immutable.List(
                Array.from({ length: placeholderCount }, () => null),
            );
            allRenderedData = allRenderedData.set(displayGroup, placeholders);
        });
        setRenderedData(allRenderedData);

        let firstCategoryFinished = false;
        const renderedDataBySceneId: Record<string, ImmutableMap<SwapSceneDesignRenderedData>> = {};
        const onSceneRendered = (sceneId: string) => {
            if (abortController.signal.aborted) {
                return;
            }
            const data = renderedDataBySceneId[sceneId];
            if (!data) {
                return;
            }
            const displayGroup = data.get('displayGroup');
            const sceneDesignList = allRenderedData.get(displayGroup);
            if (!sceneDesignList) {
                return;
            }
            const updatedList = sceneDesignList.map((sceneDesign) => {
                if (sceneDesign?.get('slide_id') === sceneId) {
                    return sceneDesign.update('isRendered', () => true);
                }
                return sceneDesign;
            });
            if (!firstCategoryFinished && updatedList.every((data) => !!data?.get('isRendered'))) {
                setTimingData('LUMINARY_RENDERED_FIRST_CATEGORY_8');
                firstCategoryFinished = true;
            }
            allRenderedData = allRenderedData.set(displayGroup, updatedList);
            setRenderedData(allRenderedData);
        };
        localLuminary.on('onRenderedSlideImage', onSceneRendered);

        setTimingData('LUMINARY_STARTING_RENDER_5');
        let renderedDataUpdated = false;
        localLuminary.batchRun(() => {
            for (const [
                displayGroup,
                sceneDesignsInDisplayGroup,
            ] of orderedByDisplayGroup.entries()) {
                if (abortController.signal.aborted) {
                    break;
                }
                const renderedData = call(
                    setupSceneGroupForSceneDesigns,
                    sceneId,
                    localLuminary,
                    sourceSentenceToUseInstead,
                    sceneAttributes,
                    faceDetections,
                    sceneDesignsInDisplayGroup.toJS(),
                );

                if (renderedData.size !== 0) {
                    // Update the structure with actual data for this display group
                    allRenderedData = allRenderedData.set(displayGroup, renderedData);
                    renderedData.forEach((data) => {
                        renderedDataBySceneId[data.get('slide_id')] = data;
                    });
                    renderedDataUpdated = true;
                }
            }

            const compatibleSceneSequenceDesigns: SceneSequenceDesign[] = (() => {
                const allSceneSequenceDesigns = selectSceneSequenceDesigns(
                    getState(),
                    luminary.template.id,
                );
                const swapVoiceoverBaseSceneDesignId = getTemplateDefaultVoiceoverSceneDesign(
                    getState(),
                    luminary.template.id,
                );
                return allSceneSequenceDesigns.filter((seq) => {
                    const firstMember = seq.membersByAspectRatio[luminary.aspectRatio]?.[0];
                    if (!firstMember) return false;
                    if (!canUserSeeSceneDesign(getState(), firstMember.sceneDesignId)) return false;
                    if (firstMember.sceneDesignId === swapVoiceoverBaseSceneDesignId) return false;

                    const sceneDesignInfo = localLuminary.getSceneDesignInfo(
                        firstMember.sceneDesignId,
                    );
                    return sceneSwapper.canSwapToCategory(sceneDesignInfo.sceneCategory);
                });
            })();
            const sceneSequenceDesigns = (() => {
                const sequenceSceneIds = getPrePackagedSceneSequenceSceneIds(originalScene);
                const lastSceneInSequence =
                    sequenceSceneIds.length > 1
                        ? luminary.getSceneById(sequenceSceneIds[sequenceSceneIds.length - 1])
                        : null;
                const rangeEndTime = lastSceneInSequence
                    ? getVoicedrivenSceneEndTime(lastSceneInSequence)
                    : getVoicedrivenSceneEndTime(originalScene);

                const originalSceneGroup = originalScene.sceneGroup;
                if (!originalSceneGroup) {
                    return compatibleSceneSequenceDesigns;
                }
                const wordCount = call(
                    getWordCountInVideoTimeRangeOperation,
                    originalSceneGroup.id,
                    getVoicedrivenSceneStartTime(originalScene),
                    rangeEndTime,
                );
                if (wordCount === null) return compatibleSceneSequenceDesigns;

                return compatibleSceneSequenceDesigns.filter(
                    (d) => (d.membersByAspectRatio[luminary.aspectRatio]?.length ?? 0) <= wordCount,
                );
            })();
            if (sceneSequenceDesigns.length > 0) {
                const chainPreviews = call(
                    loadSceneSequencePreviewsIntoLocalLuminaryOperation,
                    localLuminary,
                    sceneSequenceDesigns,
                    sceneId,
                    sourceSentenceToUseInstead,
                    sceneAttributes,
                );

                const sceneSequenceDisplayGroup =
                    DYNAMIC_DISPLAY_GROUPS[SceneDesignSpecialCase.SceneSequence];
                const chainRenderedData = Immutable.List(
                    chainPreviews.map((preview, idx) => {
                        const sceneSequenceDesign = sceneSequenceDesigns[idx];
                        const aspectRatioMembers =
                            sceneSequenceDesign.membersByAspectRatio[luminary.aspectRatio];
                        const entry = ImmutableFromJS<SwapSceneDesignRenderedData>({
                            sceneDesign: aspectRatioMembers[0].sceneDesignId,
                            sceneCategory: '',
                            displayGroup: sceneSequenceDisplayGroup,
                            displayName: sceneSequenceDesign.displayName ?? undefined,
                            slide_id: preview.primaryPreviewSceneId,
                            specialCase: SceneDesignSpecialCase.SceneSequence,
                            isActive: false,
                            isRendered: false,
                            sceneSequenceId: sceneSequenceDesign.sceneSequenceId,
                            sceneSequenceSceneIds: preview.previewSceneIds,
                        });
                        renderedDataBySceneId[preview.primaryPreviewSceneId] = entry;
                        return entry;
                    }),
                );

                if (chainRenderedData.size > 0) {
                    allRenderedData = allRenderedData.set(
                        sceneSequenceDisplayGroup,
                        chainRenderedData,
                    );
                    renderedDataUpdated = true;
                }
            }

            // Chart swap replaces the scene's contents with a chart, so it's offered
            // wherever a regular swap is — except morph mode, since chart swaps don't
            // morph from the original, and outro scenes, which show outro designs instead.
            const defaultSceneDesignId = getTemplateDefaultSceneDesign(
                getState(),
                luminary.template.id,
            );
            if (
                !morphOnlyMode &&
                defaultSceneDesignId &&
                !isOutroSceneGroup(originalScene.sceneGroup)
            ) {
                const chartDisplayGroup = DYNAMIC_DISPLAY_GROUPS[SceneDesignSpecialCase.Chart];
                const chartEntries = Immutable.List(
                    CHART_SCENE_TILES.map(({ chartType, config }) => {
                        const { scene: chartScene } = localLuminary.addSceneGroup({
                            sceneDesignId: defaultSceneDesignId,
                        });
                        call(setupChartSceneOperation, chartScene.id, chartType, config, {
                            luminary: localLuminary,
                        });
                        const entry = ImmutableFromJS<SwapSceneDesignRenderedData>({
                            sceneDesign: defaultSceneDesignId,
                            sceneCategory: chartScene.sceneCategory!,
                            displayGroup: chartDisplayGroup,
                            slide_id: chartScene.id,
                            specialCase: SceneDesignSpecialCase.Chart,
                            isActive: false,
                            isRendered: false,
                            chartType,
                        });
                        renderedDataBySceneId[chartScene.id] = entry;
                        return entry;
                    }),
                );
                if (chartEntries.size > 0) {
                    allRenderedData = allRenderedData.set(chartDisplayGroup, chartEntries);
                    renderedDataUpdated = true;
                }
            }

            if (renderedDataUpdated) {
                setRenderedData(allRenderedData);
            }
            setTimingData('LUMINARY_SCENES_ADDED_6');
        });
        setTimingData('LUMINARY_AUTO_DRAW_ENABLED_7');

        await localLuminary.onAllSlideImagesRendered();
        localLuminary.off('onRenderedSlideImage', onSceneRendered);

        allRenderedData = allRenderedData.map((sceneDesignList) =>
            sceneDesignList.map((sceneDesign) =>
                sceneDesign ? sceneDesign.set('isRendered', true) : sceneDesign,
            ),
        ) as SwapSceneDesignTabRenderedDataMap;
        setRenderedData(allRenderedData);
    },
);

export const setupLocalLuminaryOperation = createLuminaryOperation(
    async (
        { luminary, getState, call },
        sceneId: string,
        localLuminary: Luminary,
        sourceSentenceToUseInstead: string | undefined,
        sceneAttributes: Record<SceneDesignId, SceneAttributesResponse> | null,
        faceDetections: BoundingBox[],
        _setTimingData: (key: keyof TimingDataType) => void,
    ) => {
        await call(initializeLocalLuminaryOperation, localLuminary);

        const orderedSceneDesigns = call(getOrderedSceneDesigns, sceneId, faceDetections);

        const renderedList = localLuminary.batchRun(() => {
            return call(
                setupSceneGroupForSceneDesigns,
                sceneId,
                localLuminary,
                sourceSentenceToUseInstead,
                sceneAttributes,
                faceDetections,
                orderedSceneDesigns,
            );
        });

        // Convert List to OrderedMap for backward compatibility (keyed by slide_id)
        let renderedMap: Immutable.OrderedMap<
            string,
            ImmutableMap<SwapSceneDesignRenderedData>
        > = Immutable.OrderedMap();
        renderedList.forEach((sceneDesign) => {
            const slideId = sceneDesign.get('slide_id');
            renderedMap = renderedMap.set(slideId, sceneDesign);
        });

        return renderedMap;
    },
);

export const swapSceneDesignOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        localLuminary: Luminary,
        renderedData: ImmutableMap<SwapSceneDesignRenderedData>,
        sourceSentenceToUseInstead: string | undefined,
        sceneAttributes: Record<SceneDesignId, SceneAttributesResponse> | null,
        faceDetections: BoundingBox[],
    ) => {
        const chartType = renderedData.get('chartType');
        if (chartType) {
            const swapSceneDesignSlideId = getSwapSceneDesignSlideId(getState());
            if (!swapSceneDesignSlideId) {
                Toast.error('The scene was deleted or changed. Please try again.');
                return;
            }
            const sourceScene = luminary.getSceneById(swapSceneDesignSlideId);
            if (!sourceScene) {
                return;
            }
            const sourceSceneGroup = sourceScene.sceneGroup;
            if (!sourceSceneGroup) {
                return;
            }
            const chartConfig = CHART_SCENE_TILES.find(
                (tile) => tile.chartType === chartType,
            )?.config;
            if (!chartConfig) {
                return;
            }
            return call(runVoicedrivenBatchRunOperation, sourceSceneGroup.id, () =>
                call(setupChartSceneOperation, swapSceneDesignSlideId, chartType, chartConfig),
            ) as AddChartAssetResult | null;
        }

        const sceneSequenceId = renderedData.get('sceneSequenceId');
        if (sceneSequenceId) {
            const sceneSequenceSceneIds: string[] =
                renderedData.get('sceneSequenceSceneIds')?.toJS?.() ?? [];
            if (sceneSequenceSceneIds.length < 2) return;

            const sceneSequenceMembers = sceneSequenceSceneIds
                .map((previewSceneId) => {
                    const previewScene = localLuminary.getSceneById(previewSceneId);
                    if (!previewScene) {
                        return null;
                    }
                    const previewSceneGroup = previewScene.sceneGroup;
                    if (!previewSceneGroup) {
                        return null;
                    }
                    return {
                        sceneDesignId: previewScene.sceneDesign,
                        serializedSceneGroupData: previewSceneGroup.save(),
                        serializedSceneData: previewScene.save(),
                        duration: previewSceneGroup.duration,
                        durationProportion: 0,
                    };
                })
                .filter(isNotNull);
            const totalDuration = sceneSequenceMembers.reduce((sum, m) => sum + m.duration, 0);
            for (const member of sceneSequenceMembers) {
                member.durationProportion =
                    totalDuration > 0
                        ? member.duration / totalDuration
                        : 1 / sceneSequenceMembers.length;
            }

            const templateId = luminary.template.id;
            const existingDesigns = selectSceneSequenceDesigns(getState(), templateId);
            const existingDesign = existingDesigns.find(
                (d) => d.sceneSequenceId === sceneSequenceId,
            );

            const sceneSequenceDesign: SceneSequenceDesign = {
                sceneSequenceId,
                displayName: renderedData.get('displayName') ?? null,
                totalDuration,
                videoIdsByAspectRatio:
                    existingDesign?.videoIdsByAspectRatio ??
                    ({ [luminary.aspectRatio]: 0 } as Record<AspectRatio, number>),
                videoDataHashesByAspectRatio:
                    existingDesign?.videoDataHashesByAspectRatio ??
                    ({ [luminary.aspectRatio]: '' } as Record<AspectRatio, string>),
                membersByAspectRatio: {
                    [luminary.aspectRatio]: sceneSequenceMembers,
                } as Record<AspectRatio, typeof sceneSequenceMembers>,
            };

            call(
                swapToSceneSequenceOperation,
                localLuminary,
                sceneSequenceDesign,
                sourceSentenceToUseInstead,
                sceneAttributes,
                faceDetections,
            );
            return;
        }

        const targetScene = localLuminary.getSceneById(renderedData.get('slide_id'));

        const swapSceneDesignSlideId = getSwapSceneDesignSlideId(getState());
        if (!swapSceneDesignSlideId) {
            // the scene was deleted or somehow changed? close the selector
            Toast.error('The scene was deleted or changed. Please try again.');
            return;
        }

        const sourceScene = luminary.getSceneById(swapSceneDesignSlideId);
        if (!targetScene || !sourceScene) {
            // oops, invalid scene!
            return;
        }
        const initialSceneCategory = sourceScene.sceneCategory;
        if (!initialSceneCategory) {
            return;
        }
        const initialSceneDesignId = sourceScene.sceneDesign;
        const sourceSceneGroup = sourceScene.sceneGroup;
        if (!sourceSceneGroup) {
            return;
        }
        const swapSceneDesignSceneGroupId = sourceSceneGroup.id;
        const targetSceneCategory = targetScene.sceneCategory;
        if (!targetSceneCategory) {
            return;
        }

        // Detect pre-packaged scene sequence before the swap replaces scene data
        const morphPosition = getPrePackagedMorphPosition(sourceScene);
        const isChainStart = morphPosition === 'start';
        let chainSecondaryGroupIds: string[] = [];
        let chainCombinedDuration = 0;
        const voiceDrivenSequenceSceneIds = getPrePackagedSceneSequenceSceneIds(sourceScene);
        const isVoiceDrivenSequence =
            isChainStart &&
            isVoiceDrivenSceneGroup(sourceSceneGroup) &&
            voiceDrivenSequenceSceneIds.length > 1;
        const isVoiceDrivenSwap = isVoiceDrivenSceneGroup(sourceSceneGroup);
        // Captured before any mutations: the visual span the swap target must
        // cover after the swap completes. For a chain, this is the sum of each
        // chain scene's transition-stripped duration; for a single voicedriven
        // scene, just its own. Used below to keep sceneGroup.duration aligned
        // with mediaAsset.duration even when the swap changes which scene has
        // a transition (and therefore which scene has a negative timeline
        // offset).
        const voiceDrivenLogicalDuration = isVoiceDrivenSwap
            ? voiceDrivenSequenceSceneIds.reduce((sum, id) => {
                  const s = luminary.getSceneById(id);
                  return s ? sum + getVoicedrivenSceneDuration(s) : sum;
              }, 0)
            : 0;
        if (isChainStart && !isVoiceDrivenSequence) {
            const allChainIds = getPrePackagedSceneSequenceSceneGroupIds(
                luminary,
                swapSceneDesignSceneGroupId,
            );
            chainSecondaryGroupIds = allChainIds.slice(1);
            chainCombinedDuration = allChainIds.reduce((sum, id) => {
                return sum + (luminary.getSceneGroupById(id)?.duration ?? 0);
            }, 0);
        }

        const { scene: newScene } = localLuminary.addSceneGroup({
            sceneDesignId: targetScene.sceneDesign,
        });

        const sceneSwapper = new SceneSwapper(sourceScene, sourceSentenceToUseInstead);
        const assetPairs = sceneSwapper.copyContentToScene(newScene, {
            maxSubscenesToCopy: MAX_SUBSCENES_IN_PREVIEW,
        });

        const assetPairsWithCustomTiming = call(
            copyAssetTimingOverridesAfterSwapOperation,
            assetPairs,
            {
                sourceSceneId: sourceScene.id,
                targetSceneId: newScene.id,
                sourceLuminary: luminary,
                targetLuminary: localLuminary,
            },
        );
        call(applyRecommendedSceneOverridesOperation, newScene.id, sceneAttributes, {
            luminary: localLuminary,
        });

        if (renderedData.get('specialCase') === SceneDesignSpecialCase.MultipleSpeakers) {
            call(setUpMultipleSpeakersSceneOperation, newScene.id, faceDetections, {
                luminary: localLuminary,
            });
        }

        const parentActionGroup = uuid();
        const systemTriggered = false;
        const saveCheckpoint = false;

        call(runVoicedrivenBatchRunOperation, swapSceneDesignSceneGroupId, () => {
            // replaceSceneGroup serializes the newScene's SceneGroup and .load()'s that into the swapSceneDesignSceneGroup
            if (sceneSwapper.swapWillAlterSceneGroup(targetSceneCategory)) {
                // some swaps will alter things at the SceneGroup level, so we need to replace the entire SceneGroup.
                call(
                    replaceSceneGroupOperation,
                    swapSceneDesignSceneGroupId,
                    newScene.sceneGroup!,
                    systemTriggered,
                    parentActionGroup,
                    saveCheckpoint,
                );
            } else {
                // most swaps will only alter the Scene, so we can just replace the Scene. In this case we actually
                // don't want to replace the whole SceneGroup, as we don't want to affect other Scenes that might be in the SceneGroup.
                call(
                    replaceSlideOperation,
                    swapSceneDesignSlideId,
                    newScene,
                    assetPairsWithCustomTiming.map((p) => p.targetAssetId),
                    systemTriggered,
                    parentActionGroup,
                    saveCheckpoint,
                );
            }

            if (isVoiceDrivenSwap) {
                for (let i = voiceDrivenSequenceSceneIds.length - 1; i >= 1; i--) {
                    sourceSceneGroup.removeScene(voiceDrivenSequenceSceneIds[i]);
                }

                const swappedScene = luminary.getSceneById(swapSceneDesignSlideId);
                if (swappedScene) {
                    setVoicedrivenSceneDuration(swappedScene, voiceDrivenLogicalDuration);
                }
            }
        });

        if (
            targetSceneCategory === VideoData.CATEGORY_TALKING_HEAD &&
            initialSceneCategory !== VideoData.CATEGORY_TALKING_HEAD
        ) {
            void call(applyTalkingHeadMediaOperation, {
                sceneGroupId: swapSceneDesignSceneGroupId,
                media: undefined, // use existing media
                language_code: undefined, // unknown language code
                forceCaptionGeneration: false,
                enhanceAudio: false,
                recommendOverlays: true,
                systemTriggered,
                parentActionGroup,
            });
        }

        // Clear any user overrides for the scene that are changed
        // when switching scene designs. That's ALL of the user overrides
        // EXCEPT for duration.
        const isAutomatic = true;
        dispatch(
            setTextPositionUserOverride({
                sceneId: swapSceneDesignSlideId,
                isAutomatic,
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setTextSizeUserOverride({
                sceneId: swapSceneDesignSlideId,
                isAutomatic,
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setTextAlignmentUserOverride({
                sceneId: swapSceneDesignSlideId,
                isAutomatic,
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setCropUserOverride({
                sceneId: swapSceneDesignSlideId,
                isAutomatic,
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setKenburnUserOverride({
                sceneId: swapSceneDesignSlideId,
                isAutomatic,
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setBrightnessUserOverride({
                sceneId: swapSceneDesignSlideId,
                isAutomatic,
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setBackgroundColorUserOverride({
                sceneId: swapSceneDesignSlideId,
                isAutomatic,
                systemTriggered,
                parentActionGroup,
            }),
        );

        // If the swapped scene was a pre-packaged scene sequence start,
        // delete the secondary chain members and set the combined duration
        if (isChainStart && chainSecondaryGroupIds.length > 0) {
            // Delete secondary chain members in reverse order
            for (let i = chainSecondaryGroupIds.length - 1; i >= 0; i--) {
                call(
                    deleteSceneGroupOperation,
                    chainSecondaryGroupIds[i],
                    systemTriggered,
                    parentActionGroup,
                );
            }
            // Set the primary scene's duration to the combined chain duration
            if (chainCombinedDuration > 0) {
                call(
                    applyUserSceneDurationOperation,
                    swapSceneDesignSlideId,
                    chainCombinedDuration,
                    systemTriggered,
                    parentActionGroup,
                );
            }
        }

        call(checkpointVideoOperation, { systemTriggered, parentActionGroup });

        analytics.track('Scene design swapped', {
            sceneId: swapSceneDesignSlideId,
            sceneGroupId: swapSceneDesignSceneGroupId,
            sceneDesignId: initialSceneDesignId,
            newSceneDesignId: newScene.sceneDesign,
            newSceneCategory: newScene.sceneCategory,
            templateId: localLuminary.template.id,
            hasIcon: sceneContainsIcon(newScene),
        });
        dispatch(
            clearTrackedSlideTextAssets({
                sceneId: swapSceneDesignSlideId,
            }),
        );

        void call(fetchAndSetRecommendedSceneAttributesOperation, {
            sceneId: swapSceneDesignSlideId,
            systemTriggered,
            parentActionGroup,
        });
    },
);

export const createMultiSpeakerSceneOperation = createLuminaryOperation(
    (
        { luminary, call, getState },
        sceneGroupId: string,
        originalSceneId: string,
        faceDetections: BoundingBox[],
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const templateId = luminary.template.id;
        const voiceoverBaseSceneDesignId = getTemplateDefaultVoiceoverSceneDesign(
            getState(),
            templateId,
        );
        const sceneDesignIds = getTemplateSceneIds(getState(), templateId).filter(
            (sceneDesignId) => {
                const sceneDesignInfo = luminary.getSceneDesignInfo(sceneDesignId);
                const sceneCategory = sceneDesignInfo.sceneCategory;
                return (
                    canUserSeeSceneDesign(getState(), sceneDesignId) &&
                    sceneDesignId !== voiceoverBaseSceneDesignId &&
                    isVoiceDrivenCompatibleSceneCategory(sceneCategory)
                );
            },
        );
        const multipleSpeakersSceneDesignIds = call(
            getMultipleSpeakersSceneDesignIdsOperation,
            sceneDesignIds,
            faceDetections,
        );
        if (multipleSpeakersSceneDesignIds.size === 0) {
            throw new CreateOverlayError('No scene designs found for multiple speakers');
        }

        const orginalScene = luminary.getSceneById(originalSceneId);
        if (!orginalScene) {
            throw new CreateOverlayError('Original Scene missing');
        }
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new CreateOverlayError('Scene Group missing');
        }
        if (!isTalkingHeadSceneGroup(sceneGroup)) {
            throw new CreateOverlayError('Scene Group is not a Talking Head scene group');
        }
        const voiceDrivenAsset = getVoiceDrivenAsset(sceneGroup);
        if (!voiceDrivenAsset) {
            throw new CreateOverlayError('Voice Driven asset missing');
        }
        const sceneIndex = orginalScene.localIndex;

        call(runVoicedrivenBatchRunOperation, sceneGroup.id, () => {
            const newOverlayScene = sceneGroup.addScene({
                sceneDesignId: multipleSpeakersSceneDesignIds.first(),
            });
            setVoicedrivenSceneDuration(newOverlayScene, getVoicedrivenSceneDuration(orginalScene));
            sceneGroup.moveSceneToIndex(newOverlayScene.id, sceneIndex + 1);
            call(setUpMultipleSpeakersSceneOperation, newOverlayScene.id, faceDetections);
            sceneGroup.removeScene(orginalScene.id);

            const analyticsPayload: TimingMetadata = {
                method: 'crop-modal',
                hasMedia: sceneContainsEditableMedia(newOverlayScene),
                sceneId: newOverlayScene.id,
                sceneGroupId: sceneGroup.id,
                sceneDesignId: multipleSpeakersSceneDesignIds.first(),
                duration:
                    getVoicedrivenSceneEndTime(newOverlayScene) -
                    getVoicedrivenSceneStartTime(newOverlayScene),
                templateId: luminary.template.id,
                totalScenesInSceneGroup: sceneGroup.scenes.length,
                sceneGroupType: 'talking-head',
                creationDisplayGroupName: 'Multiple Speakers',
                mediaType: 'none',
                recommendedMediaSource: null,
                isVirtualDesign: false,
                isMultipleSpeakersScene: true,
            };
            analytics.track('Overlay added', analyticsPayload);
        });

        call(checkpointVideoOperation, systemTriggered, parentActionGroup);
    },
);

export const addMorphSceneOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        localLuminary: Luminary,
        renderedData: ImmutableMap<SwapSceneDesignRenderedData>,
        sourceSentenceToUseInstead: string | undefined,
        sceneAttributes: Record<SceneDesignId, SceneAttributesResponse> | null,
        faceDetections: BoundingBox[],
    ) => {
        const originalSceneId = getSwapSceneDesignSlideId(getState());
        if (!originalSceneId) {
            Toast.error('The scene was deleted or changed. Please try again.');
            return;
        }

        const originalScene = luminary.getSceneById(originalSceneId);
        if (!originalScene) {
            return;
        }
        const originalSceneGroup = originalScene.sceneGroup;
        if (!originalSceneGroup) {
            return;
        }

        const duplicatedSceneGroupId = call(duplicateSceneGroupOperation, {
            sceneGroupId: originalSceneGroup.id,
            scrollToNewSceneGroup: false,
            systemTriggered: true,
        });
        if (!duplicatedSceneGroupId) {
            return;
        }

        const duplicatedSceneGroup = luminary.getSceneGroupById(duplicatedSceneGroupId);
        if (!duplicatedSceneGroup) {
            return;
        }
        const duplicatedScene = duplicatedSceneGroup.scenes[0];
        if (!duplicatedScene) {
            return;
        }

        dispatch(setSwapSceneDesignForSlide({ sceneId: duplicatedScene.id }));

        call(
            swapSceneDesignOperation,
            localLuminary,
            renderedData,
            sourceSentenceToUseInstead,
            sceneAttributes,
            faceDetections,
        );

        const lastOriginalScene = originalSceneGroup.scenes[originalSceneGroup.scenes.length - 1];
        if (lastOriginalScene) {
            lastOriginalScene.morphEnabled = true;
        }

        const sceneToFocus = duplicatedSceneGroup.scenes[0];
        if (sceneToFocus) {
            dispatch(setFocusedSceneAndScroll(sceneToFocus.id, duplicatedSceneGroupId));
        }

        call(checkpointVideoOperation, { systemTriggered: true, parentActionGroup: uuid() });

        analytics.track('Morph scene design added', {
            sceneId: originalSceneId,
            sceneGroupId: originalSceneGroup.id,
            newSceneGroupId: duplicatedSceneGroupId,
            newSceneDesignId: renderedData.get('sceneDesign'),
        });
    },
);
